import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { PencilIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useApiHelper from "../../hooks/useApiHelper";
import useCategoryGroups from "../../data/CategoryGroup/useCategoryGroups";
import useCategoryGroupMutations from "../../data/CategoryGroup/useCategoryGroupMutations";
import { IPartCategory } from "../../models/Category/Category";
import { IDefaultPaginatedResponse } from "../../models/DefaultResponse";
import { ICategoryGroup } from "../../models/CategoryGroup/CategoryGroup";

interface CategoryGroupFormValues {
  newGroupName: string;
  categorySearch: string;
}

export default function CategoryGroups() {
  const { setInfo } = usePageNameContext();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Category Group");
  const { get } = useApiHelper();
  const { categoryGroups } = useCategoryGroups({ pageNumber: 0, itemsPerPage: 500 });
  const { create, updateCategories, remove } = useCategoryGroupMutations();
  const { register, watch, setValue } = useForm<CategoryGroupFormValues>();

  const [editOpen, setEditOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ICategoryGroup | null>(null);
  const [draftCategoryIds, setDraftCategoryIds] = useState<string[]>([]);

  const categories = useQuery({
    queryKey: ["all-categories-for-groups"],
    queryFn: () =>
      get<IDefaultPaginatedResponse<IPartCategory[]>>("/part/categories?pageNumber=0&itemsPerPage=5000"),
  });

  const allCategories = useMemo(() => categories.data?.data ?? [], [categories.data?.data]);
  const groups = useMemo(() => categoryGroups.data?.data ?? [], [categoryGroups.data?.data]);
  const newGroupName = watch("newGroupName") ?? "";
  const categorySearch = (watch("categorySearch") ?? "").toLowerCase().trim();

  const filteredCategories = useMemo(() => {
    if (!categorySearch) return allCategories;
    return allCategories.filter((x) => x.name?.toLowerCase().includes(categorySearch) || x.id.toLowerCase().includes(categorySearch));
  }, [allCategories, categorySearch]);

  const selectedCategories = useMemo(() => {
    if (!draftCategoryIds.length) return [];
    const lookup = new Set(draftCategoryIds);
    return allCategories.filter((x) => lookup.has(x.id));
  }, [allCategories, draftCategoryIds]);

  useEffect(() => {
    setInfo({
      name: "Category Groups",
      desc: "Create groups and assign categories",
      pageTitle: "Category Groups",
    });
  }, []);

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;

    create.mutate(
      { name: newGroupName.trim() },
      {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          setValue("newGroupName", "");
        },
      },
    );
  };

  const handleDeleteGroup = (group: ICategoryGroup) => {
    const confirmed = window.confirm(`Delete category group "${group.name}"?`);
    if (!confirmed) return;
    remove.mutate(group.id, deleteHandlers);
  };

  const handleOpenEditModal = (group: ICategoryGroup) => {
    setEditingGroup(group);
    setDraftCategoryIds(
      (group.partCategoryGroups ?? [])
        .map((x) => x.partCategoryId)
        .filter((x): x is string => !!x),
    );
    setValue("categorySearch", "");
    setEditOpen(true);
  };

  const handleToggleDraftCategory = (categoryId: string) => {
    setDraftCategoryIds((prev) =>
      prev.includes(categoryId) ? prev.filter((x) => x !== categoryId) : [...prev, categoryId],
    );
  };

  const handleSaveCategories = () => {
    if (!editingGroup) return;

    updateCategories.mutate(
      {
        categoryGroupId: editingGroup.id,
        categoryIds: draftCategoryIds,
      },
      {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          setEditOpen(false);
          setEditingGroup(null);
        },
      },
    );
  };

  if (categoryGroups.isLoading || categories.isLoading) {
    return (
      <div className="flex py-10 justify-center items-center">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <Modal open={editOpen} setOpen={setEditOpen} width="sm:max-w-4xl">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl">Edit Group Links{editingGroup ? `: ${editingGroup.name}` : ""}</h1>
        </div>

        <div className="mb-4 text-sm text-gray-700">
          <div className="font-semibold mb-1">Currently selected categories ({selectedCategories.length})</div>
          <div className="max-h-24 overflow-y-auto border border-gray-200 rounded p-2 bg-gray-50">
            {selectedCategories.length > 0
              ? selectedCategories.map((x) => x.name).join(", ")
              : "No categories selected."}
          </div>
        </div>

        <div className="mb-4">
          <TextInput
            label="Search categories"
            register={register}
            registerName="categorySearch"
            placeholder="Search by name or ID"
            cols="sm:col-span-2"
          />
        </div>

        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {filteredCategories.map((category) => (
            <label key={category.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draftCategoryIds.includes(category.id)}
                onChange={() => handleToggleDraftCategory(category.id)}
              />
              <span>{category.name}</span>
            </label>
          ))}
          {filteredCategories.length === 0 && (
            <div className="text-sm text-gray-500">No categories match your search.</div>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-50"
            onClick={() => setEditOpen(false)}
          />
          <ButtonInput
            classes=""
            label="Save Category Links"
            isSubmit={false}
            onClick={handleSaveCategories}
            disabled={updateCategories.isPending}
          />
        </div>
      </Modal>

      <div className="bg-white border border-gray-200 rounded p-4">
        <h3 className="text-lg font-semibold mb-3">Create Category Group</h3>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
          <TextInput
            label="Group name"
            register={register}
            registerName="newGroupName"
            placeholder="Enter new group name"
            cols="sm:col-span-1"
          />
          <ButtonInput
            label="Add Group"
            isSubmit={false}
            onClick={handleCreateGroup}
            disabled={create.isPending}
            classes="h-fit"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Group Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Linked Categories</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groups.length === 0 ? (
              <tr>
                <td className="px-4 py-4 text-sm text-gray-500" colSpan={3}>
                  No category groups found.
                </td>
              </tr>
            ) : (
              groups.map((group) => {
                const linkedCount = (group.partCategoryGroups ?? []).length;
                return (
                  <tr key={group.id}>
                    <td className="px-4 py-3 text-sm">{group.name}</td>
                    <td className="px-4 py-3 text-sm">{linkedCount}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-gray-100"
                          onClick={() => handleOpenEditModal(group)}
                          title="Edit links"
                        >
                          <PencilIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
                        </button>
                        <ButtonInput
                          label="Delete"
                          isSubmit={false}
                          onClick={() => handleDeleteGroup(group)}
                          disabled={remove.isPending}
                          classes="!py-1 !px-2"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
